import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/axios';
import { Plus, Edit, CreditCard, Power, PowerOff } from 'lucide-react';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const PaymentMethods = () => {
  // The add/edit popup opens on its first field rather than the close button.
  const nameField = useRef(null);
  const { hasRole } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [modalPaymentMethod, setModalPaymentMethod] = useState(null);
  const [name, setName] = useState('');
  const [requiresAccountNumber, setRequiresAccountNumber] = useState(false);
  const [requiresBankName, setRequiresBankName] = useState(false);
  const [requiresAccountTitle, setRequiresAccountTitle] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletePaymentMethod, setDeletePaymentMethod] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/payment-methods?all=true');
      setPaymentMethods(res.data.data);
    } catch (err) {
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setModalPaymentMethod(null);
    setName('');
    setRequiresAccountNumber(false);
    setRequiresBankName(false);
    setRequiresAccountTitle(false);
    setIsActive(true);
    setShowModal(true);
  };

  const openEditModal = (pm) => {
    setModalMode('edit');
    setModalPaymentMethod(pm);
    setName(pm.name);
    setRequiresAccountNumber(pm.requires_account_number || false);
    setRequiresBankName(pm.requires_bank_name || false);
    setRequiresAccountTitle(pm.requires_account_title || false);
    setIsActive(pm.is_active !== false);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name,
        requires_account_number: requiresAccountNumber,
        requires_bank_name: requiresBankName,
        requires_account_title: requiresAccountTitle,
      };

      if (modalMode === 'add') {
        await api.post('/api/payment-methods', payload);
        toast.success('Payment method added');
      } else {
        await api.put(`/api/payment-methods/${modalPaymentMethod.id}`, {
          ...payload,
          is_active: isActive,
        });
        toast.success('Payment method updated');
      }
      setShowModal(false);
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/payment-methods/${deletePaymentMethod.id}`);
      toast.success('Payment method deactivated');
      setDeletePaymentMethod(null);
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to deactivate payment method');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (pm, enable) => {
    setTogglingId(pm.id);
    try {
      await api.put(`/api/payment-methods/${pm.id}`, {
        name: pm.name,
        requires_account_number: pm.requires_account_number || false,
        requires_bank_name: pm.requires_bank_name || false,
        requires_account_title: pm.requires_account_title || false,
        is_active: enable,
      });
      toast.success(enable ? 'Payment method enabled' : 'Payment method disabled');
      fetchPaymentMethods();
    } catch (err) {
      toast.error(err.response?.data?.message || (enable ? 'Failed to enable' : 'Failed to disable'));
    } finally {
      setTogglingId(null);
    }
  };

  if (!hasRole || !hasRole('super_admin')) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-gray-600">You do not have permission to view this page.</p>
      </div>
    );
  }

  const activeCount = paymentMethods.filter(pm => pm.is_active !== false).length;
  const inactiveCount = paymentMethods.filter(pm => pm.is_active === false).length;

  const yesNoBadge = (val) => val ? (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Yes</span>
  ) : (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">No</span>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Methods</h1>
          <p className="text-gray-600">Manage payment methods for employee salaries</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="payment-methods-add-btn"
            onClick={openAddModal}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Payment Method
          </button>
        </div>
      </div>

      {/* Payment Methods Table */}
      <CTable
        columns={[
          {
            key: 'name',
            header: 'Name',
            cellClassName: 'font-medium',
            render: (pm) => pm.name,
          },
          {
            key: 'requires_account_number',
            header: 'Requires Account Number',
            render: (pm) => yesNoBadge(pm.requires_account_number),
          },
          {
            key: 'requires_bank_name',
            header: 'Requires Bank Name',
            render: (pm) => yesNoBadge(pm.requires_bank_name),
          },
          {
            key: 'requires_account_title',
            header: 'Requires Account Title',
            render: (pm) => yesNoBadge(pm.requires_account_title),
          },
          {
            key: 'status',
            header: 'Status',
            render: (pm) => pm.is_active !== false ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Inactive</span>
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            align: 'right',
            cellClassName: 'font-medium',
            render: (pm) => (
              <>
                <button
                  onClick={() => openEditModal(pm)}
                  className="p-1 text-warning-600 hover:text-warning-900"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                {pm.is_active !== false ? (
                  <button
                    data-testid="payment-methods-delete"
                    onClick={() => setDeletePaymentMethod(pm)}
                    className="p-1 text-danger-600 hover:text-danger-900 ml-2"
                    title="Disable"
                  >
                    <PowerOff className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleActive(pm, true)}
                    disabled={togglingId === pm.id}
                    className="p-1 text-success-600 hover:text-success-900 ml-2 disabled:opacity-50"
                    title="Enable"
                  >
                    {togglingId === pm.id ? (
                      <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-success-600 border-t-transparent" />
                    ) : (
                      <Power className="h-4 w-4" />
                    )}
                  </button>
                )}
              </>
            ),
          },
        ]}
        data={paymentMethods}
        rowKey="id"
        loading={loading}
        getRowProps={(pm) => ({ 'data-testid': `payment-methods-row-${pm.id}`, className: `hover:bg-gray-50 ${pm.is_active === false ? 'opacity-60' : ''}`.trim() })}
        header={
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              {paymentMethods.length} Payment Method{paymentMethods.length !== 1 ? 's' : ''}
              {paymentMethods.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({activeCount} active{inactiveCount > 0 ? `, ${inactiveCount} inactive` : ''})
                </span>
              )}
            </h3>
          </div>
        }
        emptyMessage={
          <>
            <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No payment methods found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first payment method.</p>
          </>
        }
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <CModal
          onClose={() => setShowModal(false)}
          labelledBy="payment-method-modal-title"
          initialFocus={nameField}
          dismissible={!saving}
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowModal(false)}
          >
            &times;
          </button>
          <h2 id="payment-method-modal-title" className="text-xl font-bold mb-4">
            {modalMode === 'add' ? 'Add Payment Method' : 'Edit Payment Method'}
          </h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="payment-method-name" className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                id="payment-method-name"
                type="text"
                ref={nameField}
                data-testid="payment-methods-field-name"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g., Bank Transfer, PayPal, Cash"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Requirements</label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresAccountNumber"
                  data-testid="payment-methods-field-requires-account-number"
                  checked={requiresAccountNumber}
                  onChange={(e) => setRequiresAccountNumber(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="requiresAccountNumber" className="ml-2 text-sm text-gray-700">
                  Requires Account Number
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresBankName"
                  data-testid="payment-methods-field-requires-bank-name"
                  checked={requiresBankName}
                  onChange={(e) => setRequiresBankName(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="requiresBankName" className="ml-2 text-sm text-gray-700">
                  Requires Bank Name
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresAccountTitle"
                  data-testid="payment-methods-field-requires-account-title"
                  checked={requiresAccountTitle}
                  onChange={(e) => setRequiresAccountTitle(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="requiresAccountTitle" className="ml-2 text-sm text-gray-700">
                  Requires Account Title
                </label>
              </div>
            </div>
            {modalMode === 'edit' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  data-testid="payment-methods-field-is-active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active
                </label>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="payment-methods-submit"
                className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 font-semibold"
                disabled={saving}
              >
                {saving ? (modalMode === 'add' ? 'Adding...' : 'Saving...') : (modalMode === 'add' ? 'Add Payment Method' : 'Save Changes')}
              </button>
            </div>
          </form>
        </CModal>
      )}

      {/* Delete Confirmation Modal */}
      {deletePaymentMethod && (
        <CModal
          onClose={() => setDeletePaymentMethod(null)}
          labelledBy="delete-payment-method-title"
          dismissible={!deleting}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
          data-testid="payment-methods-confirm-modal"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setDeletePaymentMethod(null)}
          >
            &times;
          </button>
          <h2 id="delete-payment-method-title" className="text-xl font-bold mb-4 text-danger-700">Deactivate Payment Method</h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to deactivate the payment method <b>{deletePaymentMethod.name}</b>?<br />
            <span className="text-gray-600 text-sm">
              This will mark it as inactive. You can reactivate it later by editing the payment method.
            </span>
          </p>
          <div className="flex justify-end gap-2">
            <button
              data-testid="payment-methods-confirm-no"
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setDeletePaymentMethod(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              data-testid="payment-methods-confirm-yes"
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default PaymentMethods;
